import { useCallback } from 'react';
import type { ElasticVehicle } from '@//app/api/search/vehicles/[id]';
import type { Market } from '@//config/domain-locale-map';
import { useLegacyTracking } from '@//providers/LegacyTrackingProvider';
import { useMarket } from '@//providers/MarketSettingsProvider';
import type { LegacyTrackAccessoryExposureEventData } from './legacy-types';
import { mapElasticVehicleToLegacyItem } from './utils';

interface UseLegacyTrackAccessoryExposureVariables {
  vehicle: ElasticVehicle;
  information: {
    context: 'productOverview';
    id: string;
    label: string;
  };
}

export const createLegacyTrackAccessoryExposure = (
  market: Market,
  variables: UseLegacyTrackAccessoryExposureVariables,
): LegacyTrackAccessoryExposureEventData => {
  const eventData: LegacyTrackAccessoryExposureEventData = {
    eventType: 'accessoryExposure',
    item: mapElasticVehicleToLegacyItem(market, variables.vehicle),
    pageType: 'product page',
    information: variables.information,
  };

  return eventData;
};

export const useLegacyTrackAccessoryExposure = () => {
  const market = useMarket();
  const { track } = useLegacyTracking();

  const trackLegacyAccessoryExposure = useCallback(
    (variables: UseLegacyTrackAccessoryExposureVariables) => {
      track({
        event: 'accessoryExposure',
        data: createLegacyTrackAccessoryExposure(market, variables),
      });
    },
    [track],
  );

  return {
    trackLegacyAccessoryExposure,
  };
};
